import React, { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";

/* Which design the exported proposal wears.
 *
 * This is the LOOK, not the structure. The setup card's "use a template" picks
 * which sections get written (ProposalSectionTemplate); this picks the fonts,
 * colours and cover of the file that comes out (ProposalTemplate, on the
 * organization). Two different things that unfortunately share a name in the
 * product, so the copy here says "design" throughout and never "template".
 *
 * It sits between finishing and downloading because the design is a property of
 * the FILE, not of the writing - asking earlier would make people choose a look
 * for something they have not read yet, and asking later means re-exporting.
 */

const ProposalTemplateCard = ({ onChoose }) => {
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState(null);

  const load = useCallback(async () => {
    const orgId = Cookies.get("selected_org_id");
    if (!orgId) {
      // No org means no saved designs — the export still works, it just uses
      // the built-in look. Not an error worth stopping the flow for.
      setItems([]);
      return;
    }
    try {
      const res = await api.get(
        `/api/v1/org/proposal-templates/?organization_id=${encodeURIComponent(orgId)}`
      );
      const list = Array.isArray(res?.data) ? res.data : res?.data?.results || [];
      setItems(list);
      // Keep the current choice across a refresh if it still exists; a reload
      // that silently moved the selection would be worse than not reloading.
      setPicked((cur) =>
        list.some((t) => t.id === cur)
          ? cur
          : (list.find((t) => t.is_default) || list[0])?.id ?? null
      );
    } catch {
      // Falling back to the built-in look is a better outcome than blocking
      // the download behind a list that would not load.
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* Re-read when the tab comes back.
   *
   * "Create a new design" opens the settings page in another tab. Without this
   * you would make one, return, and not see it — then have to redo the export
   * to pick it up. Coming back to the tab IS the signal that something may have
   * changed; asking for a refresh button as well would be asking the user to
   * tell us what we can already observe. */
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const loading = items === null;

  return (
    <Shell
      width={640}
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
          <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
          <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
          <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
          <path d="M12 2a10 10 0 0 0 0 20 2 2 0 0 0 2-2v-1a2 2 0 0 1 2-2h2a4 4 0 0 0 4-4 10 10 0 0 0-10-11Z" />
        </svg>
      }
      title="Choose a design"
      meta={
        !loading && items.length ? (
          <span className="text-[12px] tabular-nums text-[#6B6472]">
            {items.length} available
          </span>
        ) : null
      }
      footer={
        <>
          <button
            type="button"
            onClick={() => onChoose?.({ templateId: picked })}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-[#A64E9D] to-[#8E4187] px-3.5 py-1.5 text-[13px] font-semibold text-white ring-1 ring-inset ring-white/[0.16] shadow-[0_1px_2px_rgba(24,16,28,0.10),0_8px_18px_-10px_rgba(142,65,135,0.65)] transition-[transform,box-shadow,filter] duration-200 hover:-translate-y-[1px] hover:brightness-[1.04] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            Use this design
          </button>
          {/* New tab, deliberately: creating a design is a settings job, and
              navigating there in place would abandon a proposal mid-flow. The
              list re-reads itself when this tab regains focus, so the new one
              is simply there when they come back. */}
          <a
            href="/account/templates"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-[12.5px] font-medium text-[#8E4187] underline decoration-[#8E4187]/25 decoration-1 underline-offset-[3px] transition-colors hover:text-[#6F3369] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
          >
            Create a new design
            <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 4h6v6" />
              <path d="M20 4 10 14" />
              <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
            </svg>
          </a>
        </>
      }
    >
      <div className="px-4 py-3">
        {loading ? (
          <div className="space-y-2" aria-hidden="true">
            {[0, 1].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-[#18101C]/[0.05]" />
            ))}
          </div>
        ) : !items.length ? (
          <p className="text-[13px] text-[#6B6472]">
            No saved designs in this workspace — the export will use the built-in
            look.
          </p>
        ) : (
          <div role="radiogroup" aria-label="Proposal design" className="space-y-1.5">
            {items.map((t) => {
              const on = picked === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setPicked(t.id)}
                  className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
                    on
                      ? "border-[#A04A97]/55 bg-[#A04A97]/[0.05]"
                      : "border-[#18101C]/[0.10] bg-white hover:border-[#18101C]/25"
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 ${
                      on ? "border-[#A04A97]" : "border-[#18101C]/25"
                    }`}
                  >
                    {on && <span className="h-1.5 w-1.5 rounded-full bg-[#A04A97]" />}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-[#18101C]">
                      {t.name}
                    </span>
                    {t.description && (
                      <span className="block truncate text-[11.5px] text-[#77707E]">
                        {t.description}
                      </span>
                    )}
                  </span>

                  {t.is_default && (
                    <span className="flex-none rounded-full bg-[#A04A97]/10 px-2 py-[2px] text-[10.5px] font-semibold text-[#8E4187]">
                      Default
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

      </div>
    </Shell>
  );
};

export default ProposalTemplateCard;
